import * as React from 'react';
import { toast } from 'sonner';

/**
 * Row selection + bulk delete for a list page.
 *
 * The page owns the rows and the single-record delete mutation; this owns
 * everything between them: the selected set, the confirm dialog's state, the
 * sequential delete loop, and the ONE toast that summarises it.
 *
 * Deletes run one at a time through the entity's existing single-delete
 * endpoint (there is no bulk endpoint), so a failure part-way through does not
 * abandon the rest. Whatever failed stays selected, so the operator can see
 * exactly what is left and retry it; a clean run clears the selection.
 *
 * Pair with `BulkSelectionToolbar` (the "N selected / Delete / Clear" strip)
 * and `BulkDeleteDialog` (the confirmation, typed above
 * `BULK_DELETE_TYPED_CONFIRM_THRESHOLD`).
 */

/** Above this many records, the confirmation also asks for a typed phrase. */
export const BULK_DELETE_TYPED_CONFIRM_THRESHOLD = 10;
export const BULK_DELETE_CONFIRM_PHRASE = 'DELETE';

/** How many failed names the summary toast spells out before "and N more". */
const MAX_NAMED_FAILURES = 3;

export interface BulkDeleteNoun {
  singular: string;
  plural: string;
}

/** "1 service" / "3 services". */
export function formatBulkCount(count: number, noun: BulkDeleteNoun): string {
  return `${count} ${count === 1 ? noun.singular : noun.plural}`;
}

export interface UseBulkDeleteOptions {
  /**
   * Deletes ONE record and rejects on failure — normally the entity hook's
   * `delete…Async`. Call that hook with `showSuccessToast: false` and
   * `showErrorToast: false`, or every row raises its own toast on top of the
   * summary.
   */
  deleteOne: (id: string) => Promise<unknown>;
  noun: BulkDeleteNoun;
  /**
   * The keys of the rows currently listed. Selected ids that are no longer
   * listed (deleted from the row menu, removed by someone else) stop counting,
   * so "N selected" never includes a row the operator cannot see.
   */
  rowIds?: readonly string[];
  /** Names a record in the partial-failure toast. Falls back to a count. */
  labelFor?: (id: string) => string | undefined;
  /**
   * The selection clears whenever this changes. Pass anything that changes
   * which rows are on screen — search, tab, sort, page, branch.
   */
  resetKey?: string;
}

export interface BulkDeleteOutcome {
  deleted: string[];
  failed: { id: string; error: Error }[];
}

export interface BulkDeleteState {
  /** The effective selection (pruned to `rowIds` when given). */
  selected: Set<string>;
  count: number;
  hasSelection: boolean;
  /** Pass as `ListPage`'s `selection` config. */
  selection: {
    selected: Set<string>;
    onSelectionChange: (selected: Set<string>) => void;
  };
  clear: () => void;

  dialogOpen: boolean;
  openDialog: () => void;
  onDialogOpenChange: (open: boolean) => void;
  confirmText: string;
  setConfirmText: (value: string) => void;
  needsTypedConfirmation: boolean;
  canConfirm: boolean;

  isDeleting: boolean;
  confirm: () => Promise<BulkDeleteOutcome>;
  noun: BulkDeleteNoun;
}

function toError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}

function describeFailures(
  failed: BulkDeleteOutcome['failed'],
  labelFor: UseBulkDeleteOptions['labelFor']
): string {
  const names = failed
    .map(({ id }) => labelFor?.(id))
    .filter((name): name is string => !!name);
  const parts: string[] = [];
  if (names.length > 0) {
    const shown = names.slice(0, MAX_NAMED_FAILURES).map((n) => `“${n}”`);
    const rest = failed.length - shown.length;
    parts.push(
      `${shown.join(', ')}${rest > 0 ? ` and ${rest} more` : ''} ${
        failed.length === 1 ? 'is' : 'are'
      } still selected.`
    );
  } else {
    parts.push(`${failed.length === 1 ? 'It is' : 'They are'} still selected.`);
  }
  // The server's own reason, not a generic "failed": it is usually the one
  // thing the operator needs to read.
  const reason = failed[0]?.error.message;
  if (reason) parts.push(reason);
  return parts.join(' ');
}

export function useBulkDelete({
  deleteOne,
  noun,
  rowIds,
  labelFor,
  resetKey,
}: UseBulkDeleteOptions): BulkDeleteState {
  const [rawSelected, setRawSelected] = React.useState<Set<string>>(
    () => new Set()
  );
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [confirmText, setConfirmText] = React.useState('');
  const [isDeleting, setIsDeleting] = React.useState(false);

  // Reset DURING render when the view changes (React's documented pattern for
  // "adjusting state when a prop changes"), so a stale selection never paints.
  const [prevResetKey, setPrevResetKey] = React.useState(resetKey);
  if (resetKey !== prevResetKey) {
    setPrevResetKey(resetKey);
    setRawSelected(new Set());
  }

  const selected = React.useMemo(() => {
    if (!rowIds) return rawSelected;
    const listed = new Set(rowIds);
    const next = new Set<string>();
    for (const id of rawSelected) if (listed.has(id)) next.add(id);
    // Keep identity when nothing was pruned so memoised consumers don't churn.
    return next.size === rawSelected.size ? rawSelected : next;
  }, [rawSelected, rowIds]);

  const count = selected.size;
  const needsTypedConfirmation = count > BULK_DELETE_TYPED_CONFIRM_THRESHOLD;
  const canConfirm =
    count > 0 &&
    !isDeleting &&
    (!needsTypedConfirmation || confirmText === BULK_DELETE_CONFIRM_PHRASE);

  const clear = React.useCallback(() => setRawSelected(new Set()), []);

  const openDialog = React.useCallback(() => {
    setConfirmText('');
    setDialogOpen(true);
  }, []);

  const onDialogOpenChange = React.useCallback((open: boolean) => {
    setDialogOpen(open);
    if (!open) setConfirmText('');
  }, []);

  const confirm = React.useCallback(async (): Promise<BulkDeleteOutcome> => {
    const ids = Array.from(selected);
    const outcome: BulkDeleteOutcome = { deleted: [], failed: [] };
    if (ids.length === 0) return outcome;

    setIsDeleting(true);
    // Sequential, not Promise.all: the single-delete endpoints were not built
    // for a burst, and one-at-a-time keeps the server's error per record.
    for (const id of ids) {
      try {
        await deleteOne(id);
        outcome.deleted.push(id);
      } catch (error) {
        outcome.failed.push({ id, error: toError(error) });
      }
    }
    setIsDeleting(false);
    setDialogOpen(false);
    setConfirmText('');

    if (outcome.failed.length === 0) {
      setRawSelected(new Set());
      toast.success(`${formatBulkCount(outcome.deleted.length, noun)} deleted`);
    } else {
      // Keep ONLY the failures selected: what succeeded is gone, and what is
      // left is exactly what still needs attention.
      setRawSelected(new Set(outcome.failed.map(({ id }) => id)));
      toast.error(
        outcome.deleted.length === 0
          ? `Couldn't delete ${formatBulkCount(outcome.failed.length, noun)}`
          : `Deleted ${outcome.deleted.length} of ${ids.length} ${noun.plural}`,
        { description: describeFailures(outcome.failed, labelFor) }
      );
    }
    return outcome;
  }, [selected, deleteOne, noun, labelFor]);

  return {
    selected,
    count,
    hasSelection: count > 0,
    selection: { selected, onSelectionChange: setRawSelected },
    clear,
    dialogOpen,
    openDialog,
    onDialogOpenChange,
    confirmText,
    setConfirmText,
    needsTypedConfirmation,
    canConfirm,
    isDeleting,
    confirm,
    noun,
  };
}
